/**
 * Mini-app icon spec — the single source of truth for a mini-app's custom
 * icon (text and/or Noto emoji on a solid/vertical-gradient squircle).
 *
 * This module is intentionally runtime-dependency-free apart from zod (like
 * the rest of `@papan/types`) and pure-JS only (no `node:crypto`, no DOM) so
 * it runs identically in the browser renderer, the API PUT route, the agent
 * tool, and the server-side PNG renderer. See
 * docs/plans/2026-07-15_(completed)_MINIAPP_ICON.md.
 */
import { z } from 'zod'
import {
  MINIAPP_ICON_DEFAULT_LUCIDE,
  MINIAPP_ICON_TEXT_MAX_GRAPHEMES,
  defaultIconSpecFor,
  graphemeCount,
  MINIAPP_ICON_TEXT_MAX_UNITS,
  stripPortableIconKeys,
  type MiniappIconSpec as MiniappIconSpecShape,
} from './miniapp-icon-core.js'

// The PURE half — geometry, the text model, defaults, colours, hashing — lives
// in `miniapp-icon-core.ts` so it can be imported WITHOUT zod (the PDApp SDK
// renders icons and cannot carry a schema library). Re-exported here so every
// existing `@papan/types` import is unchanged.
export * from './miniapp-icon-core.js'

// ---------------------------------------------------------------------------

const hexColor = z
  .string()
  .regex(/^#[0-9a-fA-F]{6}$/, 'must be a 6-digit hex color like #rrggbb')

/** Lucide icon names are kebab-case, e.g. `layout-grid`, `rocket`, `a-arrow-up`. */
const LUCIDE_ICON_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/
/** Hard storage cap on raw UTF-16 units (bounds pathological ZWJ stacking). */
// Re-exported from the zod-free half so the SDK's hand validator can restate the
// same number rather than approximate it.
const TEXT_MAX_UNITS = MINIAPP_ICON_TEXT_MAX_UNITS

/** The `bg` background: a solid fill, a 2-stop vertical gradient, or a custom
 *  uploaded image. Named so the field-wise self-heal (`coerceMiniappIconSpec`) can
 *  validate a `bg` in isolation.
 *
 *  `image` carries NO path — the asset lives at a canonical dot-prefixed file at
 *  the `.miniapp` folder root (see features/files/core/icon-assets.ts), so there's
 *  zero path-traversal surface and the spec stays tiny (well under the 64 KB manifest
 *  cap). Image backgrounds are drawn by the server-composited PNG path, never
 *  client-side from the spec. */
const bgSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('solid'), color: hexColor }).strict(),
  z
    .object({
      type: z.literal('gradient'),
      from: hexColor,
      to: hexColor,
      /**
       * Offset (0..1) at which `to` is fully reached. ABSENT ⇒ 1, i.e. the even
       * top-to-bottom ramp every gradient has always been — so no stored spec
       * changes meaning, and a user picking two colours still gets an even
       * blend unless something sets this.
       *
       * Below 1 the remainder of the tile is SOLID `to` (SVG extends the last
       * stop), which is how the default tile gives most of its area to the
       * bottom colour with only a band of the top one. This is a per-spec
       * field, deliberately NOT a change to the shared render convention:
       * skewing the ramp globally would re-interpret every gradient anyone has
       * already authored.
       */
      toStop: z.number().min(0).max(1).optional(),
    })
    .strict(),
  z.object({ type: z.literal('image') }).strict(),
])

/**
 * v1 icon spec. Serialized under the `miniappIcon` key inside a `.miniapp`
 * folder's `metadata_json`. The `bg` gradient is ALWAYS a vertical 180°
 * (top → bottom) linear ramp — that's a rendering convention, not stored.
 *
 * The foreground is AT MOST ONE of: `text` (letters/emoji, up to 30 grapheme
 * clusters, wrapped + auto-scaled to fit the squircle), `icon` (a Lucide icon
 * name), or `image: true` (a custom uploaded SVG/PNG asset, rendered by the
 * server-composited PNG path). ZERO foreground = **blank** (background only).
 * They're mutually exclusive. Older stored specs (which only ever carried `text`)
 * still validate.
 */
export const MiniappIconSpecSchema = z
  .object({
    v: z.literal(1),
    text: z.string().min(1).max(TEXT_MAX_UNITS).optional(),
    icon: z
      .string()
      .min(1)
      .max(64)
      .regex(LUCIDE_ICON_RE, 'must be a lucide icon name like "layout-grid", or "pdapp"')
      .optional(),
    // Foreground = a custom uploaded image asset (canonical dot-prefixed file at
    // the folder root, no path stored). Mutually exclusive with text/icon; absent
    // = blank/other.
    image: z.literal(true).optional(),
    emojiStyle: z.enum(['color', 'mono']).default('color'),
    // Absent = auto-contrast pick (see `autoTextColor`). Also colors a Lucide icon.
    textColor: hexColor.optional(),
    bg: bgSchema,
    /**
     * Tile silhouette. ABSENT ⇒ `portrait` (the product default). `square`
     * restores the original square squircle.
     *
     * NOT USER-EDITABLE. It was briefly surfaced in the icon editor as "Tile
     * shape" (2026-08-30) and that control was removed again (2026-09-02): the
     * product decision is that a project's tile is the portrait rectangle and
     * a user does not choose otherwise.
     *
     * It remains a DEVELOPER setting — hand-set in the app's `manifest.json`,
     * which is the PORTABLE source of truth for this whole spec
     * (`metadata_json.miniappIcon` is a derived read-cache that
     * `syncMiniappIconFromManifest` reconciles) — and settable by the agent's
     * icon tool. `portrait` is stored as ABSENT rather than written
     * explicitly, which is why removing the control needed no data migration:
     * absence already was the default.
     *
     * The schema is `.strict()`, so this MUST be declared for a manifest
     * carrying it to parse at all — an undeclared key fails the whole spec and
     * `iconSpecFromManifest` falls closed to the name-derived default, i.e. a
     * hand-edit would silently reset the author's icon.
     */
    shape: z.enum(['portrait', 'square']).optional(),
  })
  .strict()
  .superRefine((val, ctx) => {
    const hasText = typeof val.text === 'string' && val.text.length > 0
    const hasIcon = typeof val.icon === 'string' && val.icon.length > 0
    const hasImage = val.image === true
    const foregrounds = (hasText ? 1 : 0) + (hasIcon ? 1 : 0) + (hasImage ? 1 : 0)
    if (foregrounds > 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'set only one foreground: text, a lucide icon, or an uploaded image',
        path: ['icon'],
      })
      return
    }
    // foregrounds === 0 is BLANK (background only) — allowed.
    if (hasText) {
      const g = graphemeCount(val.text as string)
      if (g < 1 || g > MINIAPP_ICON_TEXT_MAX_GRAPHEMES) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `text must be 1 to ${MINIAPP_ICON_TEXT_MAX_GRAPHEMES} characters`,
          path: ['text'],
        })
      }
    }
  })

export type MiniappIconSpec = z.infer<typeof MiniappIconSpecSchema>

/**
 * Coerce a raw (untrusted) icon value into a VALID MiniappIconSpec, replacing
 * ONLY the invalid fields with defaults and KEEPING every valid one. Used by the
 * manifest self-heal so a single broken property (e.g. a bad foreground glyph)
 * doesn't wipe a `bg` (background) or `textColor` (foreground color) the author
 * set correctly.
 *
 * FORMAT-only for the `icon` foreground (kebab-case) — the server additionally
 * checks the name is a REAL Lucide icon and swaps an unknown one for the default
 * glyph (that needs the Lucide map; see `features/files/core/miniapp-icon.ts`).
 * Always returns a schema-valid spec (a final `safeParse` falls back to the full
 * name-derived default if the field-wise rebuild somehow doesn't validate).
 */
export function coerceMiniappIconSpec(raw: unknown, name: string): MiniappIconSpec {
  const def = defaultIconSpecFor(name)
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return def
  const r = raw as Record<string, unknown>

  // Background — keep a valid solid/gradient/image, else the name-derived default.
  // (An image bg is kept structurally; the CORE checks the asset file exists and
  // downgrades to a gradient if not — this pure module has no storage access.)
  const bgParsed = bgSchema.safeParse(r.bg)
  const bg = bgParsed.success ? bgParsed.data : def.bg

  // Emoji style — valid enum, else default.
  const emojiStyle: 'color' | 'mono' =
    r.emojiStyle === 'mono' ? 'mono' : r.emojiStyle === 'color' ? 'color' : def.emojiStyle

  // Text color — keep a valid hex; a present-but-invalid one falls back to the
  // default's color; an ABSENT one stays absent (auto-contrast).
  let textColor: string | undefined
  if (r.textColor === undefined) textColor = undefined
  else if (hexColor.safeParse(r.textColor).success) textColor = r.textColor as string
  else textColor = def.textColor

  // Foreground — prefer a valid text, else a FORMAT-valid Lucide icon, else an
  // image foreground (`image: true`; the core verifies the asset), else: an
  // AUTHOR-INTENDED blank (no foreground key at all) is preserved as blank; a
  // present-but-broken foreground falls back to the default glyph.
  const text = typeof r.text === 'string' ? r.text : ''
  const g = text ? graphemeCount(text) : 0
  const textOk = text.length >= 1 && text.length <= TEXT_MAX_UNITS && g >= 1 && g <= MINIAPP_ICON_TEXT_MAX_GRAPHEMES
  const iconOk =
    typeof r.icon === 'string' && r.icon.length >= 1 && r.icon.length <= 64 && LUCIDE_ICON_RE.test(r.icon)
  const foregroundAbsent = r.text === undefined && r.icon === undefined && r.image === undefined
  const fg: { text: string } | { icon: string } | { image: true } | Record<string, never> = textOk
    ? { text }
    : iconOk
      ? { icon: r.icon as string }
      : r.image === true
        ? { image: true }
        : foregroundAbsent
          ? {} // blank (background only) — author set no foreground
          : { icon: MINIAPP_ICON_DEFAULT_LUCIDE } // present-but-broken foreground

  const candidate = {
    v: 1 as const,
    emojiStyle,
    bg,
    ...(textColor !== undefined ? { textColor } : {}),
    ...fg,
  }
  const parsed = MiniappIconSpecSchema.safeParse(candidate)
  return parsed.success ? parsed.data : def
}

/**
 * A schema-valid up-to-2-char uppercase monogram derived from a mini-app's
 * display name. Used by the editor to seed the Text field when the user
 * switches a default (Lucide) icon into text mode.

// The schema and the structural type in `miniapp-icon-core.ts` describe ONE
// shape from two sides: the SDK reads the type without zod, everything else
// validates with the schema. These two assertions fail to compile the moment
// they disagree, which is the only thing keeping a zod-free consumer honest.
/** `true` only while the schema and the zod-free shape describe the same spec
 *  — assignment fails the build the moment they disagree. Exported so the
 *  check itself cannot be dropped as dead code. */
export type MiniappIconSpecMatchesSchema = [MiniappIconSpec] extends [MiniappIconSpecShape]
  ? ([MiniappIconSpecShape] extends [MiniappIconSpec] ? true : false)
  : false
export const MINIAPP_ICON_SPEC_SHAPES_AGREE: MiniappIconSpecMatchesSchema = true

/**
 * …and the same check on the KEYS, because mutual `extends` is blind to exactly
 * the drift that keeps happening here: `{ a?: X }` and `{}` are mutually
 * assignable, so every optional field this spec has gained (`toStop`, `shape`,
 * `emojiStyle`) would have slipped past the assertion above. The SDK hand-rolls
 * its validator against the structural type, citing that guard — so the guard has
 * to see an added key.
 */
export type MiniappIconSpecKeysMatch =
  [keyof MiniappIconSpec] extends [keyof MiniappIconSpecShape]
    ? ([keyof MiniappIconSpecShape] extends [keyof MiniappIconSpec] ? true : false)
    : false
export const MINIAPP_ICON_SPEC_KEYS_AGREE: MiniappIconSpecKeysMatch = true

/** Namespaced key under which the spec lives inside `files.metadata_json`. */
export const MINIAPP_ICON_METADATA_KEY = 'miniappIcon'

/** Key (next to `miniappIcon`) holding the derived cache-bust rev — a short hash
 *  of the spec + any referenced asset content hashes. Lets the client build the
 *  server-PNG URL and cache-bust it without recomputing asset hashes. Present only
 *  for asset-bearing icons. */
export const MINIAPP_ICON_REV_KEY = 'miniappIconRev'

/**
 * Read a stored icon spec out of a `.miniapp` folder's `metadata_json` string.
 * Returns `null` on ANY failure — bad JSON, missing/absent key, or an invalid
 * spec. Never throws. `metadata_json` is shared plumbing (`.shared` mirror
 * rows use it too), so foreign content must resolve to `null`, not an error.
 */
export function parseMiniappIcon(
  metadataJson: string | null | undefined,
): MiniappIconSpec | null {
  if (!metadataJson) return null
  let parsed: unknown
  try {
    parsed = JSON.parse(metadataJson)
  } catch {
    return null
  }
  if (!parsed || typeof parsed !== 'object') return null
  const raw = (parsed as Record<string, unknown>)[MINIAPP_ICON_METADATA_KEY]
  if (raw === undefined || raw === null) return null
  const result = MiniappIconSpecSchema.safeParse(raw)
  return result.success ? result.data : null
}

/**
 * A PORTABLE icon (`PortablePdappMetadata.icon`) back as a spec the icon maker can
 * EDIT, or null. The portable form carries two keys the stored spec does not —
 * the resolved `glyph` and the inlined `assets` — and the schema is `.strict()`,
 * so passing it as-is is silently rejected and the maker opens on the default.
 */
export function editableIconSpec(portable: unknown): MiniappIconSpec | null {
  if (!portable || typeof portable !== 'object') return null
  const parsed = MiniappIconSpecSchema.safeParse(stripPortableIconKeys(portable))
  return parsed.success ? parsed.data : null
}

/**
 * Merge a validated icon spec (or a removal) into a `.miniapp` folder's
 * `metadata_json` string. Read-modify-write that NEVER clobbers foreign keys —
 * the column is shared plumbing (`.shared` mirror rows use it too):
 *   - invalid / absent / non-object current JSON is treated as `{}`
 *   - `spec` non-null → set/replace the `miniappIcon` key; set `miniappIconRev`
 *     when `rev` is given (asset icons), else delete it (spec-only icons)
 *   - `spec === null` → delete both the `miniappIcon` and `miniappIconRev` keys
 * Returns the new `metadata_json` string, or `null` when the resulting object
 * is empty (so the column resets to SQL NULL rather than storing `"{}"`). Pure
 * — no I/O; the API PUT route + agent tool both feed it the row's current value.
 */
export function mergeMiniappIconMetadata(
  currentMetadataJson: string | null | undefined,
  spec: MiniappIconSpec | null,
  rev?: string | null,
): string | null {
  let base: Record<string, unknown> = {}
  if (currentMetadataJson) {
    try {
      const parsed = JSON.parse(currentMetadataJson)
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        base = parsed as Record<string, unknown>
      }
    } catch {
      // Tolerate invalid JSON — treat as an empty object, never throw.
    }
  }
  if (spec === null) {
    delete base[MINIAPP_ICON_METADATA_KEY]
    delete base[MINIAPP_ICON_REV_KEY]
  } else {
    base[MINIAPP_ICON_METADATA_KEY] = spec
    if (rev) base[MINIAPP_ICON_REV_KEY] = rev
    else delete base[MINIAPP_ICON_REV_KEY]
  }
  if (Object.keys(base).length === 0) return null
  return JSON.stringify(base)
}

// ---------------------------------------------------------------------------
// Icon asset upload limits + pick validation
// ---------------------------------------------------------------------------
//
// These live HERE (pure, dependency-free) so the editor, the upload route guard,
// and the sharp re-encoder all agree on one number. They used to be three
// independent `2 * 1024 * 1024` literals across those layers — the kind of drift
// that shows up as a client hint promising a limit the server doesn't honour.
//
// SIZING RATIONALE — the byte cap is a TRANSFER bound, not a decode bound. The
// decode is already bounded by `limitInputPixels` at 4096² (icon-assets.ts); that
// pixel guard is what stops a decompression bomb, and measurement backs it up:
// re-encoding a 4001² background costs ~78ms whether the file is 130 KB or 1 MB,
// because cost tracks PIXELS, not bytes. So squeezing the byte cap buys ~nothing
// in safety or latency, and only decides which legitimate files get refused.
//
// Measured sizes for the art people actually upload — as PICKED, before shrinking:
//     4001² JPEG q85 ........ 1.15 MB   ← a real, ordinary background
//     2048² JPEG q85 ........ 0.50 MB
//     1024² true-colour PNG . 1.25 MB   ← PNG is LOSSLESS; it dwarfs JPEG
//     2048² true-colour PNG . 4.16 MB
//     1024² flat logo PNG ... 0.03 MB
//
// The editor SHRINKS a pick to the render target before uploading it
// (features/files/ui/iconAssetDownscale.ts), so a real editor upload is ~150-350 KB
// no matter what was picked — the 4001² JPEG above arrives as ~250 KB. That is what
// makes 1 MB safe: roughly 3x the largest thing the editor actually sends, while
// still admitting a hand-rolled API upload of any sane image.
//
// Read this before raising OR lowering it. 1 MB WITHOUT the client-side shrink was
// tried and reverted: it rejected that 4001² JPEG and any true-colour PNG at the
// very dimension we render backgrounds to. The cap and the shrink are a PAIR — drop
export function parseMiniappIconRev(metadataJson: string | null | undefined): string | null {
  if (!metadataJson) return null
  try {
    const parsed = JSON.parse(metadataJson) as Record<string, unknown> | null
    const rev = parsed?.[MINIAPP_ICON_REV_KEY]
    return typeof rev === 'string' ? rev : null
  } catch {
    return null
  }
}

/**
 * Read a validated icon spec out of a parsed `manifest.json` object (the
 * PORTABLE source of truth — `manifest.icon`). Fail-closed to `null` for a
 * missing/absent/malformed icon, so a hand-edited manifest can never surface an
 * invalid spec. The manifest schema keeps `icon` permissive (`unknown`) on
 * purpose (see manifest.ts) — this is where the icon is actually validated.
 */
export function iconSpecFromManifest(manifest: unknown): MiniappIconSpec | null {
  if (!manifest || typeof manifest !== 'object') return null
  const raw = (manifest as { icon?: unknown }).icon
  if (raw === undefined || raw === null) return null
  const result = MiniappIconSpecSchema.safeParse(raw)
  return result.success ? result.data : null
}

/**
 * Build a MINIMAL but schema-valid `manifest.json` string — used to RE-CREATE a
 * mini-app's manifest when it's missing (deleted, or the app was uploaded
 * without one). Just the two required fields plus the icon: `name` (derived from
 * the folder, clamped to the schema's 1–128), `version`, and `icon` when a spec
 * is given. Any richer manifest the app needs (tools/hooks/identity) is the
 * author's to add back. 2-space indent + trailing newline to match the
 * scaffolder.
 */
export function buildMinimalMiniappManifest(
  appName: string,
  spec: MiniappIconSpec | null,
): string {
  const name = (appName.trim() || 'App').slice(0, 128)
  const obj: Record<string, unknown> = { name, version: '0.1.0' }
  if (spec) obj.icon = spec
  return JSON.stringify(obj, null, 2) + '\n'
}

/**
 * Set (`spec`) or clear (`null`) the `icon` field on a RAW `manifest.json`
 * string, preserving every other key AND its ordering (spread over the parsed
 * object). Pretty-printed with 2-space indent + trailing newline to match the
 * scaffolder's format and keep diffs clean. Returns the new JSON text, or `null`
 * when the input isn't a parseable JSON object — the caller then SKIPS the write
 * rather than risk corrupting a hand-edited manifest.
 */
export function applyIconToManifestJson(
  jsonText: string,
  spec: MiniappIconSpec | null,
): string | null {
  let parsed: unknown
  try {
    parsed = JSON.parse(jsonText)
  } catch {
    return null
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null
  const next = { ...(parsed as Record<string, unknown>) }
  if (spec === null) delete next.icon
  else next.icon = spec
  return JSON.stringify(next, null, 2) + '\n'
}

/**
 * Narrow a `metadata_json` string down to ONLY its `miniappIcon` key,
 * re-stringified as a minimal `{ miniappIcon: … }` object. For cross-tenant /
 * share listings: expose the icon spec without leaking the rest of
 * `metadata_json` (`.shared` mirror internals must not cross a tenant
 * boundary). Returns `null` when the key is absent or the JSON is invalid.
 * Does NOT re-validate the spec — that's render-time `parseMiniappIcon`'s job;
 * this is a byte-narrowing filter over untrusted-for-this-purpose plumbing.
 */
export function stripToMiniappIcon(
  metadataJson: string | null | undefined,
): string | null {
  if (!metadataJson) return null
  let parsed: unknown
  try {
    parsed = JSON.parse(metadataJson)
  } catch {
    return null
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null
  const obj = parsed as Record<string, unknown>
  const raw = obj[MINIAPP_ICON_METADATA_KEY]
  if (raw === undefined || raw === null) return null
  const out: Record<string, unknown> = { [MINIAPP_ICON_METADATA_KEY]: raw }
  // Carry the derived rev too, so cross-tenant/share rows can cache-bust an
  // asset-bearing icon (it's just a short hash — no asset bytes cross).
  const rev = obj[MINIAPP_ICON_REV_KEY]
  if (typeof rev === 'string') out[MINIAPP_ICON_REV_KEY] = rev
  return JSON.stringify(out)
}

// ---------------------------------------------------------------------------
// Squircle — one fixed iOS-style superellipse, shared by every renderer
// ---------------------------------------------------------------------------
//
// A 4-segment cubic-Bézier squircle on the 512×512 grid (r = 256, centered at
// 256,256), matching the curvature of the `squircle.svg` reference (control
// points ≈ 0.727·r along each axis — squarer than a circle's 0.5523, an
// iOS-style continuous corner). Transformed from that reference (center 144.5,
// r 100) by (v−144.5)·2.56 + 256. Static so the CSS clip-path, the client SVG,
// and the server sharp render all clip to identical geometry.

// ---------------------------------------------------------------------------
// Tile geometry — ONE resolver for the two tile shapes
// ---------------------------------------------------------------------------